import { supabase } from './supabase';
import { toast } from 'react-hot-toast';
import { initiatePlatformAuth } from './platformAuth';

const PLATFORM_CONFIGS = {
  'meta-ads': {
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    scope: 'ads_read,business_management,pages_read_engagement',
    responseType: 'code',
    envKey: 'VITE_META_ADS_CLIENT_ID',
    redirectUri: window.location.hostname === 'localhost' 
      ? `${window.location.origin}/#/dashboard/integrations/callback`
      : 'https://unifyads.io/#/dashboard/integrations/callback'
  },
  'shopify': {
    authUrl: 'https://accounts.shopify.com/oauth',
    scope: 'read_orders,read_products,read_customers,read_analytics',
    responseType: 'code',
    envKey: 'VITE_SHOPIFY_CLIENT_ID',
  },
} as const;

export type PlatformId = keyof typeof PLATFORM_CONFIGS;

interface ConnectionState {
  loading: boolean;
  error: string | null;
  requiresConfiguration?: boolean;
}

export const initiatePlatformConnection = async (
  platformId: PlatformId,
  userId: string
): Promise<ConnectionState> => {
  try {
    console.log('Initiating connection for platform:', platformId);
    
    if (platformId === 'meta-ads') {
      await initiatePlatformAuth('meta-ads');
      return { loading: true, error: null };
    }

    const config = PLATFORM_CONFIGS[platformId];
    const clientId = import.meta.env[config.envKey];

    if (!clientId) {
      console.error('Missing client ID for platform:', platformId);
      return {
        loading: false,
        error: 'Platform connection is not configured',
        requiresConfiguration: true
      };
    }

    // Generate state
    const state = crypto.randomUUID();
    
    // Get redirect URI based on platform
    const redirectUri = config.redirectUri || (
      window.location.hostname === 'localhost'
        ? `${window.location.origin}/#/dashboard/integrations/callback`
        : 'https://unifyads.io/#/dashboard/integrations/callback'
    );
    
    console.log('Using redirect URI:', redirectUri);

    // Store state and platform info
    const { error: stateError } = await supabase
      .from('platform_oauth_states')
      .insert({
        user_id: userId,
        platform_id: platformId,
        state,
        credentials: { 
          client_id: clientId,
          redirect_uri: redirectUri
        }
      });

    if (stateError) {
      console.error('Failed to store OAuth state:', stateError);
      throw stateError;
    }

    // Build OAuth URL
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: config.responseType,
      scope: config.scope,
      state,
    });

    const authUrl = `${config.authUrl}?${params.toString()}`;
    console.log('Redirecting to auth URL:', authUrl);
    window.location.href = authUrl;
    return { loading: true, error: null };
  } catch (error) {
    console.error('Connection error:', error);
    return {
      loading: false,
      error: error instanceof Error ? error.message : 'Failed to initiate connection',
    };
  }
};

export const handlePlatformCallback = async (
  params: URLSearchParams
): Promise<ConnectionState> => {
  try {
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    
    console.log('Processing callback with params:', {
      hasCode: !!code,
      hasState: !!state,
      error,
      errorDescription
    });
    
    if (error) {
      console.error('OAuth error:', { error, errorDescription });
      throw new Error(errorDescription || error);
    }
    
    if (!code || !state) {
      throw new Error('Missing required parameters');
    }

    // Verify state and get stored data
    const { data: oauthState, error: stateError } = await supabase
      .from('platform_oauth_states')
      .select('user_id, platform_id, credentials')
      .eq('state', state)
      .single();

    if (stateError || !oauthState) {
      console.error('State verification failed:', stateError);
      throw new Error('Invalid or expired connection request');
    }

    console.log('OAuth state verified for platform:', oauthState.platform_id);

    if (oauthState.platform_id === 'meta-ads') {
      // Exchange code for token
      const tokenResponse = await fetch('https://graph.facebook.com/v18.0/oauth/access_token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: oauthState.credentials?.client_id,
          client_secret: import.meta.env.VITE_META_ADS_CLIENT_SECRET,
          redirect_uri: oauthState.credentials?.redirect_uri,
          code,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        console.error('Token exchange failed:', errorData);
        throw new Error(errorData.error?.message || 'Failed to exchange code for access token');
      }

      const { access_token, expires_in } = await tokenResponse.json();
      console.log('Successfully exchanged code for token');

      // Get ad account information
      const accountResponse = await fetch(
        'https://graph.facebook.com/v18.0/me/adaccounts?fields=account_id,name,business_name,account_status',
        {
          headers: { 'Authorization': `Bearer ${access_token}` }
        }
      );

      if (!accountResponse.ok) {
        const accountError = await accountResponse.json();
        console.error('Failed to fetch ad accounts:', accountError);
        throw new Error('Failed to access ad accounts. Please ensure you have granted all required permissions.');
      }

      const accountData = await accountResponse.json();
      
      if (!accountData.data?.length) {
        throw new Error('No Meta Ads accounts found. Please make sure you have access to an Ads account.');
      }

      // Find an active ad account (account_status = 1 means active)
      const activeAccount = accountData.data.find((account: any) => account.account_status === 1);
      if (!activeAccount) {
        throw new Error('No active Meta Ads accounts found. Please make sure you have an active ads account.');
      }

      // Store the connection
      const { error: connectionError } = await supabase
        .from('platform_connections')
        .upsert({
          user_id: oauthState.user_id,
          platform_id: oauthState.platform_id,
          platform_type: 'advertising',
          credentials: {
            access_token,
            expires_at: new Date(Date.now() + expires_in * 1000).toISOString(),
            account_id: activeAccount.account_id,
            account_name: activeAccount.name,
            business_name: activeAccount.business_name,
            account_status: activeAccount.account_status,
          },
          is_active: true,
          connected_at: new Date().toISOString(),
          last_sync: new Date().toISOString(),
        }, {
          onConflict: 'user_id,platform_id',
        });

      if (connectionError) {
        console.error('Failed to store connection:', connectionError);
        throw connectionError;
      }
    }

    // Clean up state
    await supabase
      .from('platform_oauth_states')
      .delete()
      .eq('state', state);

    return { loading: false, error: null };
  } catch (error) {
    console.error('Callback error:', error);
    return {
      loading: false,
      error: error instanceof Error ? error.message : 'Failed to complete connection',
    };
  }
};